import { DEFAULT_TRAILING_MONTHS } from "./constants";
import { seedLegDefaults } from "./legSeed";
import type { DatePeriodValues } from "./types";
import type { DatePeriodKind, DateRangeKind } from "~/graphql/supergraphTypes";

// Helpers that null out fields that no longer apply and seed defaults for the
// new variant when `periodKind` or `customRangeKind` change. Centralized so
// each body stays a dumb input renderer and the BE `_per_kind_invariants`
// validator is always satisfied (notably: every CUSTOM leg has exactly one of
// `parameter` / `date` set immediately after the transition).

const hasStartLeg = (current: DatePeriodValues): boolean =>
  current.customStartParameter !== null || current.customStartDate !== null;

const hasEndLeg = (current: DatePeriodValues): boolean =>
  current.customEndParameter !== null || current.customEndDate !== null;

const startLegOrSeed = (
  current: DatePeriodValues,
): Pick<DatePeriodValues, "customStartParameter" | "customStartDate"> => {
  if (hasStartLeg(current)) {
    return {
      customStartParameter: current.customStartParameter,
      customStartDate: current.customStartDate,
    };
  }
  const seed = seedLegDefaults();
  return { customStartParameter: seed.parameter, customStartDate: seed.date };
};

const endLegOrSeed = (
  current: DatePeriodValues,
): Pick<DatePeriodValues, "customEndParameter" | "customEndDate"> => {
  if (hasEndLeg(current)) {
    return {
      customEndParameter: current.customEndParameter,
      customEndDate: current.customEndDate,
    };
  }
  const seed = seedLegDefaults();
  return { customEndParameter: seed.parameter, customEndDate: seed.date };
};

export const reconcileForPeriodKind = (
  current: DatePeriodValues,
  next: DatePeriodKind,
): DatePeriodValues => {
  if (next === "TRAILING_MONTHS") {
    return {
      ...current,
      periodKind: next,
      trailingMonths: current.trailingMonths ?? DEFAULT_TRAILING_MONTHS,
      customRangeKind: null,
      customStartParameter: null,
      customStartDate: null,
      customEndParameter: null,
      customEndDate: null,
    };
  }
  if (next === "CUSTOM") {
    const customRangeKind = current.customRangeKind ?? "SINGLE";
    return {
      ...current,
      periodKind: next,
      trailingMonths: null,
      customRangeKind,
      ...startLegOrSeed(current),
      ...(customRangeKind === "RANGE"
        ? endLegOrSeed(current)
        : { customEndParameter: null, customEndDate: null }),
    };
  }
  return {
    ...current,
    periodKind: next,
    trailingMonths: null,
    customRangeKind: null,
    customStartParameter: null,
    customStartDate: null,
    customEndParameter: null,
    customEndDate: null,
  };
};

export const reconcileForCustomRangeKind = (
  current: DatePeriodValues,
  next: DateRangeKind,
): DatePeriodValues => {
  if (next === "SINGLE") {
    return {
      ...current,
      customRangeKind: next,
      customEndParameter: null,
      customEndDate: null,
    };
  }
  return {
    ...current,
    customRangeKind: next,
    ...endLegOrSeed(current),
  };
};

const PERIOD_VALUE_KEYS: ReadonlyArray<keyof DatePeriodValues> = [
  "periodKind",
  "dateType",
  "offsetMonths",
  "trailingMonths",
  "customRangeKind",
  "customStartParameter",
  "customStartDate",
  "customEndParameter",
  "customEndDate",
];

export const arePeriodValuesEqual = (a: DatePeriodValues, b: DatePeriodValues): boolean =>
  PERIOD_VALUE_KEYS.every((key) => a[key] === b[key]);

/** Idempotent normalizer that re-runs `reconcileForPeriodKind` against the
 * current `periodKind`. Used on mount to defend against parents seeding the
 * sub-tree with a state that already violates `_per_kind_invariants` (e.g.
 * `CUSTOM` with `customRangeKind === null`, `TRAILING_MONTHS` with
 * `trailingMonths === null`, or a CUSTOM leg with both fields null). */
export const normalizePeriod = (current: DatePeriodValues): DatePeriodValues =>
  reconcileForPeriodKind(current, current.periodKind);
